// Pure "drag over a chart -> dashboard date range" logic (design doc's
// drag-to-zoom feature). The pointer-to-bucket-index half of this mechanism
// is @bi/shared's chart-hover.ts (shared with the floating hover
// tooltip, per the brief: build the mapping ONCE); this module owns the
// BI-specific second half — which charts may even be dragged, and what a
// bucket INDEX range means as a calendar DATE range — since only this app's
// charts carry real, dashboard-narrowable calendar buckets.
import type { BucketIndexMode } from "@bi/shared";
import type { ChartSpec, DatasetSpec } from "@bi/shared/types";

import { type TimeGrain, grainSeconds, isSubDayGrain, parseBucket } from "./bucket-format";
import { chartIgnoresDateRange } from "./filter-state";

/**
 * Whether dragging on THIS chart can produce a date-range zoom. Gates, in order:
 * - viz_type must be `echarts_timeseries_bar`/`_line`. `big_number*`,
 *   `gauge_chart`, `pie` and `table` have no left-to-right bucket axis.
 * - An unresolved dataset (`undefined`, e.g. mid-load) is NOT brushable. This
 *   deliberately diverges from `chartIgnoresDateRange`, which returns `false`
 *   for it: "cannot tell" must never enable a drag.
 * - `chartIgnoresDateRange` (filter-state.ts) mirrors the backend's
 *   `resolve_temporal_column` type gate, so a categorical x_axis (`stage_label`,
 *   `outcome`, `budget_bucket`) never produces a date filter.
 * - A real sample, `buckets[0]`, must parse with `bucketLabelToDateString`. The
 *   declared `bq_type` does not show the serialized label shape, and without
 *   this check a chart can look brushable while every drag does nothing. Empty
 *   `buckets` is not brushable. Sharing one parser with
 *   `dateBoundsFromBucketRange` keeps the gate and the conversion in step.
 */
export function isBrushable(
  chart: ChartSpec,
  dataset: DatasetSpec | undefined,
  buckets: readonly string[],
): boolean {
  if (chart.viz_type !== "echarts_timeseries_bar" && chart.viz_type !== "echarts_timeseries_line") {
    return false;
  }
  if (dataset === undefined) return false;
  if (chartIgnoresDateRange(chart, dataset)) return false;
  const sample = buckets[0];
  return sample !== undefined && bucketLabelToDateString(sample) !== null;
}

/** "step" for `echarts_timeseries_bar` (BarChart's own layout: `n` equal-
 *  width steps, no padding), "nearest" for `echarts_timeseries_line`
 *  (LineAreaChart's `n` evenly spaced points) — the exact same two modes
 *  `chart-hover.ts`'s `bucketIndexFromFraction` already distinguishes for
 *  the hover tooltip on these same two primitives. */
export function bucketModeForChart(chart: ChartSpec): BucketIndexMode {
  return chart.viz_type === "echarts_timeseries_bar" ? "step" : "nearest";
}

/** Two bucket indices from a drag, in DRAG order (start may be after end —
 *  a right-to-left drag) -> the same range, ascending. A drag that never
 *  moved (start === end) is a valid single-bucket selection, not collapsed
 *  away here — the caller decides whether a single bucket is a large enough
 *  movement to count as a drag at all (a pixel-distance threshold, which
 *  needs real DOM measurement this pure module deliberately has no part of). */
export function normalizeIndexRange(a: number, b: number): { lo: number; hi: number } {
  return a <= b ? { lo: a, hi: b } : { lo: b, hi: a };
}

export interface DateBounds {
  start: string;
  end: string;
}

/** Matches `YYYY-MM-DD` or a fuller ISO 8601 timestamp (the shapes BigQuery
 *  `DATE`/`DATETIME`/`TIMESTAMP` serialize to over JSON). Group 1 is the date.
 *  Stricter than `Date`, which accepts a bare `"2024"` or `"Jan"` and would turn
 *  a categorical bucket into a fake date.
 *
 *  `bucketLabelToDateString` returns the captured digits and never round-trips
 *  through `new Date(...)`:
 *  - `TIMESTAMP` serializes with a numeric offset (`'2026-08-09T00:00:00+00:00'`),
 *    so the offset group accepts `[+-]HH:MM`, not only `Z`.
 *  - A zoneless `'2026-08-09 00:00:00'` would parse as LOCAL time and could
 *    shift the date by a day in a non-UTC browser. The BI stack is UTC. */
const ISO_DATE_MATCH =
  /^(\d{4}-\d{2}-\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;

function bucketLabelToDateString(label: string): string | null {
  return ISO_DATE_MATCH.exec(label)?.[1] ?? null;
}

/**
 * The bucket range `[lo, hi]` (inclusive, index-aligned with `buckets`,
 * ascending per `normalizeIndexRange`) -> the `{start, end}` date bounds for
 * `FilterState.time_range_start`/`_end` (a plain `date`, the shape
 * `filter-bar.tsx`'s presets write). `compiler.py` compiles this as
 * `DATE(col) BETWEEN @start AND @end` (inclusive), so the last bucket's date is
 * already the correct upper bound: no "+1 day".
 *
 * Returns `null` when either label does not parse as a date or an index is out
 * of range (empty result, or a drag that outlived a data change). Never a
 * partial pair: the backend refuses a one-sided override
 * (`PartialTimeRangeOverride`).
 */
export function dateBoundsFromBucketRange(
  buckets: readonly string[],
  loIndex: number,
  hiIndex: number,
): DateBounds | null {
  const loLabel = buckets[loIndex];
  const hiLabel = buckets[hiIndex];
  if (loLabel === undefined || hiLabel === undefined) return null;
  const start = bucketLabelToDateString(loLabel);
  const end = bucketLabelToDateString(hiLabel);
  if (start === null || end === null) return null;
  // A reversed pair can still occur here if the underlying bucket LABELS
  // aren't monotonically ordered with their indices (not expected of a
  // real compiled result, but this function doesn't trust that from the
  // caller) — normalize once more on the resolved dates themselves so the
  // returned bounds are always start <= end.
  return start <= end ? { start, end } : { start: end, end: start };
}

/**
 * The same bucket range, at the chart's own bucket precision.
 *
 * Day-or-coarser grains delegate to `dateBoundsFromBucketRange`. A sub-day
 * grain (`PT1M`/`PT1H`) emits MOMENT bounds, because day bounds turn a 30-minute
 * drag into the whole day. `FilterState.time_range_start`/`_end` accept moments
 * (see `lib/time-range.ts`). Both bounds carry the same precision: the backend
 * refuses a mixed pair (`MixedTimeRangePrecision`).
 *
 * The upper bound is the END of the last bucket (label + width - 1s). A label is
 * the bucket's opening moment and the backend compiles an inclusive `BETWEEN`,
 * so the raw label would drop the rest of the final bucket.
 *
 * Returns `null` under the same conditions as `dateBoundsFromBucketRange`.
 */
export function timeBoundsFromBucketRange(
  buckets: readonly string[],
  loIndex: number,
  hiIndex: number,
  grain: TimeGrain | null,
): DateBounds | null {
  if (!isSubDayGrain(grain)) return dateBoundsFromBucketRange(buckets, loIndex, hiIndex);
  const loLabel = buckets[loIndex];
  const hiLabel = buckets[hiIndex];
  if (loLabel === undefined || hiLabel === undefined) return null;
  const lo = parseBucket(loLabel);
  const hi = parseBucket(hiLabel);
  if (lo === null || hi === null) return null;
  const loMs = Date.UTC(lo.year, lo.month - 1, lo.day, lo.hour, lo.minute, lo.second);
  const hiMs = Date.UTC(hi.year, hi.month - 1, hi.day, hi.hour, hi.minute, hi.second);
  const [startMs, endMs] = loMs <= hiMs ? [loMs, hiMs] : [hiMs, loMs];
  // `grain` is a real sub-day grain here (isSubDayGrain narrowed it), so its
  // bucket width is defined.
  const widthMs = grainSeconds(grain as TimeGrain) * 1000;
  return { start: toWireMoment(startMs), end: toWireMoment(endMs + widthMs - 1000) };
}

/** Milliseconds since epoch -> the `YYYY-MM-DDTHH:MM:SS` wire moment shape
 *  (`lib/time-range.ts`'s `DATE_TIME`, and the backend's own
 *  `_TIME_OF_DAY_IN_STRING`). UTC by construction: the input was built with
 *  `Date.UTC` and `toISOString` reads back in UTC, so no local-zone shift is
 *  possible at either end. The trailing `Z` is dropped because the wire
 *  carries no offset and reads every bound as UTC by convention. */
function toWireMoment(ms: number): string {
  return new Date(ms).toISOString().slice(0, 19);
}
